import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Card } from "shared/components/Card";
import logoSquareWhite from "shared/branding/logo-square-omni-color.svg";
import { ChecklistItem } from "./components/ChecklistItem";
import { CollapsedPill } from "./components/CollapsedPill";
import { useOnboardingChecklistState } from "./hooks";
import { useDraggable } from "./useDraggable";
import {
  ONBOARDING_CHECKLIST_COLLAPSED_KEY,
  ONBOARDING_CHECKLIST_TITLE
} from "./constants";
import headerBackground from "./assets/checklist-header-bg.jpg";

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { checklist, isAvailable, refetch } = useOnboardingChecklistState();
  const drag = useDraggable();
  const { containerRef, dragStyle, handleProps } = drag;
  const [collapsedOverride, setCollapsedOverride] = useState<boolean | null>(
    () =>
      Number(sessionStorage.getItem(ONBOARDING_CHECKLIST_COLLAPSED_KEY))
        ? true
        : null
  );

  if (!isAvailable || !checklist) return null;

  const progress = Math.round(
    (checklist.completed_count / checklist.total_count) * 100
  );

  // Opens on its own until everything is done, then only on request.
  const isCollapsed =
    collapsedOverride ?? checklist.completed_count === checklist.total_count;

  const handleCollapse = () => {
    sessionStorage.setItem(ONBOARDING_CHECKLIST_COLLAPSED_KEY, "1");
    setCollapsedOverride(true);
  };

  // Reopening is the gesture right after finishing a task, so pick up the result.
  const handleExpand = () => {
    sessionStorage.removeItem(ONBOARDING_CHECKLIST_COLLAPSED_KEY);
    setCollapsedOverride(false);
    void refetch();
  };

  // Get out of the way of the screen the user was just sent to work on.
  const handleManage = (route: string) => {
    handleCollapse();
    navigate(route);
  };

  if (isCollapsed)
    return (
      <CollapsedPill
        completedCount={checklist.completed_count}
        totalCount={checklist.total_count}
        progress={progress}
        onExpand={handleExpand}
        drag={drag}
      />
    );

  return (
    <div
      ref={containerRef}
      style={dragStyle}
      className="fixed inset-x-4 bottom-4 z-[12] max-w-[530px] md:bottom-6 md:left-[104px] md:right-6"
    >
      <Card className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden !shadow-xl md:max-h-[629px]">
        <header
          {...handleProps}
          className="relative shrink-0 cursor-grab overflow-hidden px-4 pb-6 pt-4 active:cursor-grabbing"
        >
          <img
            src={headerBackground}
            alt=""
            className="pointer-events-none absolute inset-0 size-full object-cover"
          />
          <div className="relative flex items-center justify-between gap-2.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <img
                src={logoSquareWhite}
                alt=""
                className="pointer-events-none size-[30px] shrink-0"
              />
              <p className="truncate text-base font-bold leading-6 text-content-1000-inverted">
                {ONBOARDING_CHECKLIST_TITLE}
              </p>
            </div>
            <span data-drag-ignore className="shrink-0">
              <IconButton
                aria-label="Minimize onboarding checklist"
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                HugeIcon={Cancel01Icon}
                borderLess
                className="!bg-transparent !stroke-white !text-content-1000-inverted hover:!bg-alpha-100"
                onClick={handleCollapse}
              />
            </span>
          </div>
          <div className="relative flex items-center gap-4 pl-1 pr-2">
            <div className="h-2.5 flex-1 overflow-hidden rounded-lg bg-content-100-inverted">
              <div
                className="h-full rounded-lg bg-content-1000-inverted transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-base font-bold leading-6 text-content-1000-inverted">
              {checklist.completed_count}/{checklist.total_count}
            </p>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[71px] bg-[linear-gradient(to_bottom,rgba(255,255,255,0)_23.66%,#ffffff_100%)]" />
        </header>
        <ul className="scroll-on-hover min-h-0 flex-1 divide-y divide-alpha-100 overflow-y-auto px-6 pb-8 pt-2">
          {checklist.items.map((item) => (
            <ChecklistItem key={item.key} item={item} onManage={handleManage} />
          ))}
        </ul>
      </Card>
    </div>
  );
};
